import React, { useState, useMemo, useEffect } from 'react';
import { X, ArrowUpRight, ArrowDownRight, AlertTriangle, Sparkles, RefreshCw } from 'lucide-react';
import { formatMoney } from '../../utils/format';
import { getDriverPhoto } from '../../utils/driverPhoto';
import { PERSONALITY_TRAITS } from '../../data/personalityTraits';
import useGameStore from '../../store/gameStore';
import { useTranslation } from '../../i18n';
import { localize } from '../../data/i18nData';

/**
 * Negotiation modal — replaces instant hire. Lets the player pitch a salary
 * within a slider, see the live reaction band (refusal / resentful / fair /
 * generous), and submit. Backend logic in hrSlice.proposeHireDriver.
 *
 * 05/02/2026 — Q1 retour user prod : ajout d'un round unique de
 * contre-proposition. Quand l'offre est entre 80 % et 100 % de l'attendu,
 * le candidat propose un compromis. Le joueur peut accepter (en remontant
 * son offre) ou refuser (rupture définitive).
 */
export function HireNegotiationModal({ driver: rawDriver, onClose, onAccept }) {
  const { t } = useTranslation();
  const driver = useMemo(() => localize('driver', rawDriver), [rawDriver]);
  const proposeHireDriver = useGameStore((s) => s.proposeHireDriver);
  const computeExpectedSalary = useGameStore((s) => s.computeExpectedSalary);
  const expected = useMemo(() => computeExpectedSalary(driver.id), [computeExpectedSalary, driver.id]);
  // Player starts the slider at the candidate's expected salary.
  const [offer, setOffer] = useState(expected);
  const [result, setResult] = useState(null);
  // Counter-offer state (round 2). When set, the slider min is the counter.
  const [counterOffer, setCounterOffer] = useState(null);

  // Bump slider to the counter when one is received so the player can react.
  useEffect(() => {
    if (counterOffer) setOffer(counterOffer);
  }, [counterOffer]);

  const ratio = offer / expected;
  let band;
  if (ratio < 0.80) band = 'refus';
  else if (ratio < 0.90) band = 'rancune';
  else if (ratio < 1.05) band = 'ok';
  else band = 'genereuse';

  const bandConfig = {
    refus: {
      color: 'text-danger',
      bg: 'bg-danger/10',
      border: 'border-danger/40',
      label: t('hn_band_refus_label'),
      detail: t('hn_band_refus_detail'),
      icon: <ArrowDownRight className="w-4 h-4" />,
    },
    rancune: {
      color: 'text-warn',
      bg: 'bg-warn/10',
      border: 'border-warn/40',
      label: t('hn_band_rancune_label'),
      detail: t('hn_band_rancune_detail'),
      icon: <AlertTriangle className="w-4 h-4" />,
    },
    ok: {
      color: 'text-accent',
      bg: 'bg-accent/10',
      border: 'border-accent/40',
      label: t('hn_band_ok_label'),
      detail: t('hn_band_ok_detail'),
      icon: <Sparkles className="w-4 h-4" />,
    },
    genereuse: {
      color: 'text-money',
      bg: 'bg-money/10',
      border: 'border-money/40',
      label: t('hn_band_genereuse_label'),
      detail: t('hn_band_genereuse_detail'),
      icon: <ArrowUpRight className="w-4 h-4" />,
    },
  };
  const config = bandConfig[band];

  const sliderMin = Math.round(expected * 0.70);
  const sliderMax = Math.round(expected * 1.25);

  const handleSubmit = () => {
    const r = proposeHireDriver(driver.id, offer);
    // 05/02/2026 — Q1 : Round contre-proposition (un seul autorisé).
    if (!r.ok && r.reason === 'counter_offer') {
      setCounterOffer(r.counterSalary);
      // Pas de setResult → on reste dans l'UI active mais avec un bandeau
      // « contre-proposition » par-dessus.
      return;
    }
    setResult(r);
    if (r.ok) {
      setTimeout(() => onAccept?.(r), 1200);
    }
  };

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/50 backdrop-blur-sm p-4" data-testid="hire-negotiation-modal">
      <div className="bg-ink-800 rounded-xl w-full max-w-lg shadow-2xl overflow-hidden border border-ink-600">
        <div className="px-5 py-4 border-b border-ink-600 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <img
              src={getDriverPhoto(driver.id)}
              alt={driver.name}
              className="w-12 h-12 rounded-full object-cover border border-ink-600"
              onError={(e) => { e.target.style.display = 'none'; }}
            />
            <div>
              <h3 className="font-bold text-sm text-slate-100">{driver.name}</h3>
              <p className="text-[10px] text-slate-300">{t('hn_subtitle')}</p>
            </div>
          </div>
          <button aria-label="Fermer" onClick={onClose} className="text-slate-300 hover:text-slate-100" data-testid="close-negotiation">
            <X className="w-4 h-4" />
          </button>
        </div>

        {result?.ok === false ? (
          <div className="p-5">
            <div className="rounded-lg border border-danger/40 bg-danger/10 p-4 text-sm text-danger">
              <p className="font-bold mb-1">
                {result.reason === 'counter_refused' ? 'Négociation rompue' /* i18n-keep */ : t('hn_offer_rejected')}
              </p>
              <p className="text-xs">
                {result.reason === 'counter_refused' ? (
                  <>
                    {/* i18n-keep — FR-first wording */}
                    {driver.name} avait proposé <strong>{formatMoney(result.counterSalary)}</strong> en
                    contre-proposition. Votre dernière offre {formatMoney(offer)} a été refusée.
                  </>
                ) : (
                  <>
                    {driver.name} {t('hn_expected_at_least')} {formatMoney(result.expected)} {t('hn_and_refused')} {formatMoney(offer)}.
                  </>
                )}
              </p>
              <p className="text-xs mt-2 italic">
                {t('hn_unavailable_until')} J+{result.lockUntil}.
              </p>
            </div>
            <button
              onClick={onClose}
              className="mt-4 w-full bg-ink-700 text-slate-200 py-2.5 rounded-lg text-sm font-bold hover:bg-ink-600"
              data-testid="close-rejected"
            >
              {t('hn_close')}
            </button>
          </div>
        ) : result?.ok ? (
          <div className="p-5">
            <div className="rounded-lg border border-money/40 bg-money/10 p-4 text-sm text-money">
              <p className="font-bold mb-1">{t('hn_offer_accepted')}</p>
              <p className="text-xs">
                {driver.name} {t('hn_joins_team_for')} {formatMoney(result.hiredSalary)} {t('hn_gross_per_month')}.
                {result.satisfactionBonus > 0 ? ` ${t('satisfaction') || 'Satisfaction'} +${result.satisfactionBonus}.` : ''}
                {result.satisfactionBonus < 0 ? ` ${t('satisfaction') || 'Satisfaction'} ${result.satisfactionBonus} (${t('hn_resentment')}).` : ''}
              </p>
            </div>
          </div>
        ) : (
          <div className="p-5 space-y-4">
            {/* 05/02/2026 — Q1 : bandeau contre-proposition (round 2) */}
            {counterOffer && (
              <div className="rounded-lg border border-amber-400/50 bg-amber-500/10 p-3" data-testid="counter-offer-banner">
                <p className="text-xs font-bold text-amber-200 flex items-center gap-1.5">
                  <RefreshCw className="w-3.5 h-3.5" /> Contre-proposition reçue {/* i18n-keep — FR-first */}
                </p>
                <p className="text-[11px] text-slate-300 mt-1">
                  {/* i18n-keep — FR-first negotiation tooltip */}
                  {driver.name} accepterait à <strong className="text-amber-200 font-mono">{formatMoney(counterOffer)}</strong>{t('hn_per_month_gross')}.
                  Tu peux remonter ton offre ≥ ce montant (acceptation) ou rester en dessous (rupture définitive, cooldown 60-90 j).
                </p>
              </div>
            )}
            {/* Personality traits + strength/weakness */}
            <div>
              <p className="text-[10px] uppercase tracking-wider text-slate-300 font-mono mb-1.5">{t('hn_profile')}</p>
              <div className="flex flex-wrap gap-1 mb-2">
                {(driver.personalityTraits || []).map(traitId => {
                  const t = PERSONALITY_TRAITS[traitId];
                  if (!t) return null;
                  return (
                    <span key={traitId} className="text-[10px] px-1.5 py-0.5 rounded-full bg-accent/10 text-accent border border-accent/30">
                      {t.label}
                    </span>
                  );
                })}
              </div>
              <p className="text-[11px] text-money">+ {driver.strength}</p>
              <p className="text-[11px] text-danger">− {driver.weakness}</p>
            </div>

            {/* Expected vs offered */}
            <div className="bg-ink-700/30 rounded-lg p-3 grid grid-cols-2 gap-3 border border-ink-600">
              <div>
                <p className="text-[10px] uppercase tracking-wider text-slate-300 font-mono">{t('hn_expected')}</p>
                <p className="text-lg font-bold text-slate-100 font-mono" data-testid="expected-salary">{formatMoney(expected)}<span className="text-[10px] text-slate-300">{t('hn_per_month_gross')}</span></p>
              </div>
              <div>
                <p className="text-[10px] uppercase tracking-wider text-slate-300 font-mono">{t('hn_your_offer')}</p>
                <p className={`text-lg font-bold font-mono ${config.color}`} data-testid="offer-amount">{formatMoney(offer)}<span className="text-[10px] text-slate-300">{t('hn_per_month_gross')}</span></p>
              </div>
            </div>

            {/* Slider */}
            <div>
              <input
                type="range"
                min={sliderMin}
                max={sliderMax}
                step={50}
                value={offer}
                onChange={(e) => setOffer(Number(e.target.value))}
                className="w-full accent-accent"
                data-testid="salary-slider"
              />
              <div className="flex justify-between text-[10px] text-slate-300 font-mono mt-1">
                <span className="text-danger">{formatMoney(sliderMin)}</span>
                <span className="text-slate-300">{t('hn_target')} {formatMoney(expected)}</span>
                <span className="text-money">{formatMoney(sliderMax)}</span>
              </div>
            </div>

            {/* Reaction band */}
            <div className={`rounded-lg border px-3 py-2.5 ${config.bg} ${config.border}`} data-testid={`reaction-band-${band}`}>
              <p className={`text-xs font-bold flex items-center gap-1.5 ${config.color}`}>
                {config.icon} {config.label}
              </p>
              <p className="text-[11px] text-slate-200 mt-0.5">{config.detail}</p>
            </div>

            {/* Contract type info — explicit so the player understands no
                "auto-entrepreneur" toggle is offered. They are hiring a
                salaried driver, not subcontracting to a freelancer. */}
            <div
              className="rounded-lg border border-ink-600 bg-ink-700/40 px-3 py-2 text-[11px] text-slate-200 space-y-1"
              data-testid="contract-type-info"
            >
              <p className="font-semibold text-slate-100">{t('hn_contract_type')}</p>
              <p>
                <span className="inline-block px-1.5 py-0.5 rounded bg-money/15 text-money font-mono text-[10px] mr-1.5">CDI</span>
                {t('hn_cdi_full_time')}
              </p>
              <p className="text-slate-300 italic">
                {t('hn_no_freelance_note')}
              </p>
            </div>

            {/* Submit */}
            <button
              onClick={handleSubmit}
              className="w-full bg-accent text-white py-3 rounded-lg text-sm font-bold hover:bg-accent-hover transition-colors"
              data-testid="submit-offer"
            >
              {counterOffer
                ? (offer >= counterOffer
                    ? `Accepter à ${formatMoney(offer)}${t('hn_per_month')}` // i18n-keep
                    : `Réponse finale à ${formatMoney(offer)}${t('hn_per_month')} (rupture)`) // i18n-keep
                : `${t('hn_propose_at')} ${formatMoney(offer)}${t('hn_per_month')}`}
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
